import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import rewardsImage from '@/assets/filefinity_rewards.png';

function RewardsProgramTermsContent() {
  return (
    <div className="pr-4 text-sm">
      <p className="text-muted-foreground mb-6">Last Updated: April 29, 2025</p>

      <p className="text-gray-700 mb-6">
        These Rewards Program Terms &amp; Conditions govern participation in the
        FileFinity Rewards Program (the &quot;Program&quot;), operated by
        Onefinity CNC Ltd. and/or its affiliates (&quot;Onefinity,&quot;
        &quot;FileFinity,&quot; &quot;we,&quot; &quot;us,&quot; or
        &quot;our&quot;). By participating in the Program, you agree to be bound
        by these Terms.
      </p>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">1. Program Overview</h3>
        <p className="text-gray-700">
          The Program allows users to upload CNC files to FileFinity and earn
          reward points (&quot;Points&quot;) based on qualifying user ratings of
          those files. Points may be redeemed for eligible rewards, including
          but not limited to gift cards for use at Onefinity CNC.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">2. Earning Points</h3>
        <p className="text-gray-700 mb-2 font-medium">
          2.1 Eligibility for Points
        </p>
        <p className="text-gray-700 mb-2">Users may earn Points when:</p>
        <ul className="list-disc list-inside text-gray-700 mb-3">
          <li className="mb-1">
            A file uploaded by the User is downloaded by another user; and
          </li>
          <li className="mb-1">
            The downloading user submits a rating of the file; and
          </li>
          <li className="mb-1">
            The rating meets the qualification criteria outlined below.
          </li>
        </ul>
        <p className="text-gray-700 mb-2 font-medium">2.2 Rating Criteria</p>
        <ul className="list-disc list-inside text-gray-700 mb-3">
          <li className="mb-1">
            Files may be rated on multiple categories (e.g., design quality,
            ease of use, documentation, results, value), each on a 1–5 star
            scale.
          </li>
          <li className="mb-1">
            A combined average rating of 4.0–5.0 stars from an individual
            reviewing user qualifies for rewards.
          </li>
        </ul>
        <p className="text-gray-700 mb-2 font-medium">2.3 Point Award</p>
        <ul className="list-disc list-inside text-gray-700 mb-3">
          <li className="mb-1">1 Point is awarded per qualifying review.</li>
          <li className="mb-1">
            1 Point = $1 USD equivalent value for redemption purposes only.
          </li>
          <li className="mb-1">
            Points have no cash value and are not redeemable for cash.
          </li>
        </ul>
        <p className="text-gray-700 mb-2 font-medium">2.4 Limitations</p>
        <ul className="list-disc list-inside text-gray-700">
          <li className="mb-1">
            Only verified downloads are eligible to submit ratings.
          </li>
          <li className="mb-1">
            Only one rating per user per file is permitted.
          </li>
          <li className="mb-1">
            FileFinity may require minimum account age, verified activity, and
            additional engagement signals before allowing ratings or awarding
            Points.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">3. Redemption of Points</h3>
        <ul className="list-disc list-inside text-gray-700">
          <li className="mb-1">
            Minimum redemption: 10 Points required before redemption is
            permitted.
          </li>
          <li className="mb-1">
            Redemption limit: up to 500 Points per 24-hour period.
          </li>
          <li className="mb-1">
            Points may be redeemed for gift cards for use at{' '}
            <a
              href="https://www.onefinitycnc.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              www.onefinitycnc.com
            </a>
            .
          </li>
          <li className="mb-1">
            Points are non-transferable, not exchangeable for cash, and
            non-refundable.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">
          4. Content Requirements &amp; Ownership
        </h3>
        <p className="text-gray-700 mb-2">
          Users must only upload content they own or have full legal rights to
          distribute. The following is strictly prohibited: stolen, copied, or
          unauthorized files; copyrighted material without permission; content
          violating third-party rights.
        </p>
        <p className="text-gray-700">
          Files found to be in violation will be removed without notice, will
          not qualify for rewards, and may result in loss of Points or account
          suspension.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">
          5. Fraud, Abuse &amp; Manipulation
        </h3>
        <p className="text-gray-700 mb-2">Users may not:</p>
        <ul className="list-disc list-inside text-gray-700 mb-2">
          <li className="mb-1">Create multiple accounts to generate ratings</li>
          <li className="mb-1">
            Rate their own files (directly or indirectly)
          </li>
          <li className="mb-1">
            Coordinate artificial ratings or manipulation
          </li>
          <li className="mb-1">Use bots, scripts, or automated systems</li>
        </ul>
        <p className="text-gray-700">
          Enforcement actions may include removal of Points, reversal of
          redemptions, account suspension, or a permanent ban. All
          determinations are at FileFinity&apos;s sole discretion.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">
          6. Right to Modify or Terminate Program
        </h3>
        <p className="text-gray-700">
          Onefinity reserves the right at any time and without notice to modify,
          suspend, or terminate the Program; change how Points are earned or
          redeemed; adjust point values or reward structures; or revoke Points
          for any reason. Participation does not guarantee continued
          availability or value.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">7. Revocation of Rewards</h3>
        <p className="text-gray-700">
          We reserve the right to review any account, file, rating, or activity
          and revoke Points or rewards at any time for any reason, including
          suspected abuse, violations of these Terms, questionable content
          ownership, or system errors.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">8. No Guarantee of Earnings</h3>
        <p className="text-gray-700">
          The Program is not a form of employment or guaranteed income. Earnings
          depend on file quality, user engagement, and platform activity.
          FileFinity makes no guarantees regarding Points earned or rewards
          received.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">9. Limitation of Liability</h3>
        <p className="text-gray-700">
          To the fullest extent permitted by law, Onefinity and FileFinity shall
          not be liable for loss of Points, inability to redeem rewards, account
          suspension or termination, or technical issues or system
          interruptions.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">10. Account Termination</h3>
        <p className="text-gray-700">
          We may suspend or terminate any account at our sole discretion, with
          or without notice, for violations of these Terms or any related
          policies.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="font-semibold mb-2">11. General Terms</h3>
        <p className="text-gray-700">
          These Terms may be updated at any time. Continued participation
          constitutes acceptance of changes. These Terms are governed by the
          laws of the applicable jurisdiction.
        </p>
      </section>

      <section className="mb-2">
        <h3 className="font-semibold mb-2">12. Contact</h3>
        <p className="text-gray-700">
          For questions regarding the Program, contact us at{' '}
          <a
            href="mailto:support@filefinity.com"
            className="text-blue-600 hover:underline"
          >
            support@filefinity.com
          </a>
          .
        </p>
      </section>
    </div>
  );
}

interface RewardsProgramTermsModalProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onAccept?: () => void;
}

function RewardsProgramTermsModal({
  trigger,
  open: controlledOpen,
  onOpenChange,
  onAccept,
}: RewardsProgramTermsModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Rewards Program Terms &amp; Conditions</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[75vh]">
          <img
            src={rewardsImage}
            alt="FileFinity Rewards"
            className="mx-auto mb-6 max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
          />
          <RewardsProgramTermsContent />
        </ScrollArea>
        {onAccept && (
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                onAccept();
                setOpen(false);
              }}
              className="px-4 py-2 text-sm font-medium rounded-md bg-foreground text-background hover:opacity-90 transition-opacity"
            >
              I Agree
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RewardsProgramTermsModal;
